import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import AllTrainingBrochuresHeader from "@/components/molecules/table-headers/AllTrainingBrochuresHeader";
import Table from "@/components/molecules/table/Table";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import IFile from "@/interfaces/File";
import TrainingBrochure from "@/interfaces/TrainingBrochure";
import { useDownloadFileMutation } from "@/services/files";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  useDeleteTrainingBrochureMutation,
  useGetTrainingBrochuresQuery,
} from "@/services/trainingBrochures";
import { faDownload, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useRef } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const TrainingBrochuresView: FC = () => {
  const user = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);

  const [state, setState] = useTableState<Partial<TableState>>(
    "training-brochures",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const [downloadFileAsync] = useDownloadFileMutation();

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetTrainingBrochuresQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const [deleteBrochure] = useDeleteTrainingBrochureMutation();

  const onDelete = (id: string) => {
    toast.promise(deleteBrochure(id).unwrap(), {
      pending: "Suppression de la plaquette...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return "Plaquette supprimé avec succès";
        },
      },
      error: "Erreur lors de la suppression de la plaquette",
    });
  };

  const onDownload = (file: IFile) => {
    toast.promise(downloadFileAsync(file).unwrap(), {
      pending: "Téléchargement de la plaquette...",
      success: "Plaquette téléchargée avec succès",
      error: "Erreur lors du téléchargement de la plaquette",
    });
  };

  const columns = useRef<ColumnDef<TrainingBrochure>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id, file }) => (
        <Fragment>
          <OverlayTrigger
            overlay={<Tooltip id="nextTooltip">Télécharger</Tooltip>}
          >
            <Button
              variant="success"
              size="sm"
              className="border-300 me-1 text-600"
              onClick={() => onDownload(file)}
            >
              <FontAwesomeIcon icon={faDownload} color="white" />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger overlay={<Tooltip id="nextTooltip">Voir</Tooltip>}>
            <Link
              className="btn btn-sm btn-info border-300 me-1 text-600"
              to={file.link}
              target="_blank"
            >
              <FontAwesomeIcon icon={faEye} color="white" />
            </Link>
          </OverlayTrigger>
          {checkIfUserHasRoles(user, ["ADMIN", "CSM"]) && (
            <OverlayTrigger
              overlay={<Tooltip id="nextTooltip">Supprimer</Tooltip>}
            >
              <Button
                variant="danger"
                size="sm"
                className="border-300 text-600"
                onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
              >
                <FontAwesomeIcon icon={faTrash} color="white" />
              </Button>
            </OverlayTrigger>
          )}
        </Fragment>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "Label",
      footer: (props) => props.column.id,
      accessorFn: ({ label }) => label,
      id: "label",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom du fichier",
      footer: (props) => props.column.id,
      accessorFn: ({ file }) => file.name,
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Taille",
      footer: (props) => props.column.id,
      accessorFn: ({ file }) => file.size,
      id: "size",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé le fichier qui a pour id : {{id}} ?"
      />
      <Table
        resource="training-brochures"
        tableName="training-brochures"
        HeaderComponent={AllTrainingBrochuresHeader}
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default TrainingBrochuresView;
